import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Plus, MagnifyingGlass, Key, Trash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newClient, setNewClient] = useState({ email: '', password: '', full_name: '', phone: '', country: '', currency: 'USD' });

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // Auto-refresh so admin sees Online/Offline transitions without F5.
    // Only a users_col read — no Twelve Data spend, no WebTrader impact.
    const id = setInterval(fetchClients, 30000);
    return () => clearInterval(id);
  }, []);

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/clients`, newClient);
      toast.success('Client created successfully');
      setShowCreate(false);
      setNewClient({ email: '', password: '', full_name: '', phone: '', country: '', currency: 'USD' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const toggleClientStatus = async (clientId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/clients/${clientId}/status`, {
        is_active: !currentStatus,
        reason: 'Admin action'
      });
      toast.success('Client status updated');
      fetchClients();
    } catch (error) {
      toast.error('Failed to update client');
    }
  };

  const resetPassword = async (clientId) => {
    if (!window.confirm('Reset password to Welcome123!?')) return;
    try {
      const response = await axios.post(`${API}/admin/clients/${clientId}/reset-password`, { new_password: 'Welcome123!' });
      toast.success(`Password reset to: ${response.data.new_password}`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const deleteClient = async (client) => {
    const label = client.full_name || client.email;
    const confirmed = window.confirm(`Permanently delete "${label}"?\n\nThis will remove the user AND all related trading accounts, orders, positions, transactions, watchlist, KYC and support tickets.\n\nThis action cannot be undone.`);
    if (!confirmed) return;
    const typed = window.prompt(`Type "${client.email}" to confirm deletion:`);
    if (typed !== client.email) {
      toast.error('Email did not match — deletion cancelled');
      return;
    }
    try {
      const res = await axios.delete(`${API}/admin/clients/${client.id}`);
      const r = res.data?.removed || {};
      toast.success(`Deleted ${label}. Removed: ${Object.entries(r).filter(([,v])=>v).map(([k,v])=>`${v} ${k}`).join(', ') || 'user record'}.`);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const filteredClients = clients.filter(c => 
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-8" data-testid="admin-clients-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Clients</h1>
          <p className="text-slate-600 mt-1">{clients.length} total clients</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="admin-create-client-btn">
              <Plus size={18} className="mr-2" /> Create Client
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-4">
              <div>
                <Label>Full Name</Label>
                <Input required value={newClient.full_name} onChange={e => setNewClient({...newClient, full_name: e.target.value})} data-testid="admin-new-client-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" required value={newClient.email} onChange={e => setNewClient({...newClient, email: e.target.value})} data-testid="admin-new-client-email" />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" required minLength={6} value={newClient.password} onChange={e => setNewClient({...newClient, password: e.target.value})} data-testid="admin-new-client-password" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={newClient.phone} onChange={e => setNewClient({...newClient, phone: e.target.value})} />
              </div>
              <div>
                <Label>Country</Label>
                <Input value={newClient.country} onChange={e => setNewClient({...newClient, country: e.target.value})} />
              </div>
              <div>
                <Label>Account Currency</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="admin-new-client-currency-group">
                  {[{c:'USD',l:'USD — US Dollar ($)'},{c:'EUR',l:'EUR — Euro (€)'}].map(o => (
                    <button key={o.c} type="button"
                      data-testid={`admin-new-client-currency-${o.c}`}
                      onClick={() => setNewClient({...newClient, currency: o.c})}
                      className={`px-3 py-2 rounded-md text-sm border ${
                        newClient.currency === o.c
                          ? 'bg-brand-primary text-white border-brand-primary'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}>{o.l}</button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="admin-new-client-submit">Create Client</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6 flex items-center gap-2">
        <MagnifyingGlass size={20} className="text-slate-400" />
        <Input placeholder="Search by email or name..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-md" data-testid="admin-clients-search" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="overflow-x-auto">
        <table className="min-w-[900px] w-full text-sm" data-testid="admin-clients-table">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
              <th className="p-4">Name</th>
              <th className="p-4">Email</th>
              <th className="p-4">Country</th>
              <th className="p-4">KYC</th>
              <th className="p-4">Status</th>
              <th className="p-4">Online</th>
              <th className="p-4">Created</th>
              <th className="p-4">Last Seen</th>
              <th className="p-4">Last Login</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="10" className="p-8 text-center"><div className="loader mx-auto"></div></td></tr>
            ) : filteredClients.length === 0 ? (
              <tr><td colSpan="10" className="p-8 text-center text-slate-500">No clients found</td></tr>
            ) : filteredClients.map(client => (
              <tr key={client.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-4 font-medium">{client.full_name}</td>
                <td className="p-4">{client.email}</td>
                <td className="p-4">{client.country || '-'}</td>
                <td className="p-4">
                  <select
                    data-testid={`admin-kyc-select-${client.id}`}
                    value={client.kyc_status || 'NOT_STARTED'}
                    onChange={async (e) => {
                      const next = e.target.value;
                      if (next === (client.kyc_status || 'NOT_STARTED')) return;
                      if (!window.confirm(`Set ${client.full_name || client.email} KYC to ${next}?`)) return;
                      try {
                        await axios.put(`${API}/admin/clients/${client.id}/kyc-status`, { kyc_status: next, reason: 'Admin update' });
                        toast.success(`KYC set to ${next}`);
                        fetchClients();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update KYC'); }
                    }}
                    className={`px-2 py-1 rounded text-xs font-semibold border cursor-pointer ${
                      client.kyc_status === 'APPROVED' ? 'bg-trading-bullish-bg text-trading-bullish border-emerald-200' :
                      client.kyc_status === 'REJECTED' ? 'bg-trading-bearish-bg text-trading-bearish border-rose-200' :
                      'bg-yellow-50 text-yellow-800 border-yellow-200'
                    }`}
                  >
                    <option value="NOT_STARTED">NOT_STARTED</option>
                    <option value="PENDING">PENDING</option>
                    <option value="APPROVED">APPROVED</option>
                    <option value="REJECTED">REJECTED</option>
                  </select>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    client.is_active ? 'bg-trading-bullish-bg text-trading-bullish' : 'bg-trading-bearish-bg text-trading-bearish'
                  }`}>
                    {client.is_active ? 'Active' : 'Suspended'}
                  </span>
                </td>
                <td className="p-4">
                  <span
                    data-testid={`admin-client-online-${client.id}`}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold ${
                      client.is_online ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${client.is_online ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></span>
                    {client.is_online ? 'Online' : 'Offline'}
                  </span>
                </td>
                <td className="p-4 text-slate-600">{new Date(client.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-xs text-slate-600 tabular-nums">
                  {client.last_seen_at ? new Date(client.last_seen_at).toLocaleString() : '—'}
                </td>
                <td className="p-4 text-xs">
                  <a href={`/admin/clients/${client.id}/login-history`} data-testid={`admin-login-history-${client.id}`} className="underline text-brand-accent">
                    {client.last_login_at ? new Date(client.last_login_at).toLocaleString() : 'Never'}
                  </a>
                </td>
                <td className="p-4">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" asChild data-testid={`admin-open-client-${client.id}`}>
                      <a href={`/admin/clients/${client.id}`}>Open</a>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleClientStatus(client.id, client.is_active)} data-testid={`admin-toggle-status-${client.id}`}>
                      {client.is_active ? 'Suspend' : 'Activate'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => resetPassword(client.id)} data-testid={`admin-reset-password-${client.id}`}>
                      <Key size={14} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteClient(client)} data-testid={`admin-delete-client-${client.id}`} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200" title="Delete client permanently">
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default AdminClients;
